/**
 * UoM Modal Component
 * Provides accessible modal functionality with focus trapping and keyboard navigation
 */

class UomModal {
  constructor(modalIdentifier, trigger) {
    // Support both ID and data attribute selector
    if (modalIdentifier.startsWith('[') && modalIdentifier.endsWith(']')) {
      // Data attribute selector like '[data-modal-window="my-modal"]'
      this.dialog = document.querySelector(modalIdentifier);
    } else {
      // Traditional ID selector
      this.dialog = document.getElementById(modalIdentifier);
    }
    this.trigger = trigger; // Now accepts DOM element instead of ID
    this.lastFocus = null;

    this.focusableSelectors = [
      'a[href]',
      'area[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      'iframe',
      'object',
      'embed',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable]',
    ];

    this.init();
  }

  init() {
    if (!this.dialog || !this.trigger) return;

    // Bind event listeners
    this.trigger.addEventListener('click', () => this.open());
    this.dialog.addEventListener('close', () => this.handleClose());

    // Close on backdrop click
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) {
        this.close();
      }
    });

    // Keyboard navigation
    this.dialog.addEventListener('keydown', (e) => this.handleKeydown(e));
  }

  open() {
    // Store the element that triggered the modal
    this.lastFocus = document.activeElement;

    this.dialog.showModal();
    this.addFocusTrap();

    // Focus the first focusable element
    setTimeout(() => {
      this.focusFirstElement();
    }, 10);
  }

  close() {
    this.dialog.close();
  }

  handleClose() {
    this.removeFocusTrap();

    // Return focus to the trigger element
    if (this.lastFocus) {
      this.lastFocus.focus();
    }
  }

  handleKeydown(event) {
    // Close on Escape key
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    // Handle Tab key for focus trapping
    if (event.key === 'Tab') {
      this.trapTabKey(event);
    }
  }

  trapTabKey(event) {
    const focusableElements = this.getFocusableElements();
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        lastElement.focus();
        event.preventDefault();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        firstElement.focus();
        event.preventDefault();
      }
    }
  }

  addFocusTrap() {
    document.addEventListener(
      'focus',
      (this.boundTrapFocus = (e) => this.trapFocus(e)),
      true
    );
  }

  removeFocusTrap() {
    if (this.boundTrapFocus) {
      document.removeEventListener('focus', this.boundTrapFocus, true);
    }
  }

  trapFocus(event) {
    if (!this.dialog.open) return;

    if (this.dialog.contains(event.target)) {
      this.lastFocus = event.target;
    } else {
      this.focusFirstElement();
      if (this.lastFocus === document.activeElement) {
        this.focusLastElement();
      }
      this.lastFocus = document.activeElement;
      event.preventDefault();
      event.stopPropagation();
    }
  }

  getFocusableElements() {
    return Array.from(
      this.dialog.querySelectorAll(this.focusableSelectors.join(','))
    ).filter((element) => this.isFocusable(element));
  }

  focusFirstElement() {
    const focusableElements = this.getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
      return true;
    }
    return false;
  }

  focusLastElement() {
    const focusableElements = this.getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[focusableElements.length - 1].focus();
      return true;
    }
    return false;
  }

  isFocusable(element) {
    if (element.tabIndex < 0) return false;
    if (element.disabled) return false;
    if (element.offsetParent === null) return false; // Hidden elements
    return true;
  }
}

// Auto-initialize modals based on data attributes
document.addEventListener('DOMContentLoaded', () => {
  // Initialize modals with data-modal-trigger attribute
  const modalTriggers = document.querySelectorAll('[data-modal-trigger]');

  modalTriggers.forEach((trigger, index) => {
    const modalIdentifier = trigger.getAttribute('data-modal-trigger');

    if (modalIdentifier) {
      // Auto-generate ID if trigger doesn't have one
      if (!trigger.id) {
        trigger.id = `modal-trigger-${index + 1}`;
      }

      // Check if modalIdentifier looks like a data attribute selector or regular ID
      let modalSelector;
      if (modalIdentifier.includes('=')) {
        // It's a data attribute like "data-modal-window=my-modal"
        modalSelector = `[${modalIdentifier}]`;
      } else {
        // It's a regular ID
        modalSelector = modalIdentifier;
      }

      new UomModal(modalSelector, trigger);
    }
  });

  // Legacy support for old data-modal attribute (deprecated)
  const legacyTriggers = document.querySelectorAll(
    '[data-modal]:not([data-modal-trigger])'
  );

  legacyTriggers.forEach((trigger, index) => {
    const modalId = trigger.getAttribute('data-modal');

    if (modalId) {
      // Auto-generate ID if trigger doesn't have one
      if (!trigger.id) {
        trigger.id = `legacy-modal-trigger-${index + 1}`;
      }

      new UomModal(modalId, trigger);
    }
  });

  // Legacy support for specific modal IDs (deprecated)
  const specificTrigger = document.getElementById(
    'uom-2035-video-modal-trigger'
  );
  const specificModal = document.getElementById('uom-2035-video-modal');

  if (
    specificTrigger &&
    specificModal &&
    !specificTrigger.hasAttribute('data-modal-trigger')
  ) {
    new UomModal('uom-2035-video-modal', specificTrigger);
  }
});

// Export for manual initialization
window.UomModal = UomModal;
